import axios from 'axios-observable';
import { AxiosObservable } from 'axios-observable/dist/axios-observable.interface';
import { PageableQuery, TalosPayload, UUID } from '../app/basicModels';
import { PhenomenonPage, Phenomenon, PhenomenonSimple, ClonedCharacteristicsIndicators, CloneCharacteristicsIndicatorsDTO } from './models';
import { buildWorkspaceConfig } from '../methodologyDeep/scope';
import CONFIG from '../../settings';
import { CommandRequest } from '../../helpers/request/models';
import { hasOperation, ObservationsConfigV2 } from '../observationsConfigV2';

const phenomenonUrl = `${CONFIG.apiUrl}:${CONFIG.demeterPort}/api/v1/phenomenons`;
const phenomenaApiUrl = `${CONFIG.apiUrl}:${CONFIG.demeterPort}/api/v1/phenomena`;
const phenomenaUrl = `${CONFIG.apiUrl}:${CONFIG.demeterPort}/v1/phenomena`;

export const getPhenomenons = (_query: PageableQuery, companyId: UUID, showCanonicals: boolean): AxiosObservable<PhenomenonPage> => {
  if (companyId) {
    return axios.get<PhenomenonPage>(
      `${phenomenonUrl}?page=${_query.pageable.page}&size=${_query.pageable.size}&company_id=${companyId}&show_canonicals=${showCanonicals}`
    );
  }
  return axios.get<PhenomenonPage>(
    `${phenomenonUrl}?page=${_query.pageable.page}&size=${_query.pageable.size}&show_canonicals=${showCanonicals}&shallow=true`
  );
};

export const getPhenomenon = (phenomenonId: UUID): AxiosObservable<Phenomenon> => {
  return axios.get<Phenomenon>(`${phenomenonUrl}/by-id/${phenomenonId}`, {
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate'
    }
  });
};

export const getPhenomenonsSimple = (ids: UUID[]): AxiosObservable<PhenomenonSimple> => {
  return axios.post<PhenomenonSimple>(`${phenomenaApiUrl}/ids/simple`, { ids });
};

export const savePhenomenon = (
  phenomenon: TalosPayload<Phenomenon>,
  isNew: boolean,
  migratedWrites = '[]'
): AxiosObservable<Phenomenon> => {
  if (isNew) {
    return createPhenomenon(phenomenon, migratedWrites);
  }
  return updatePhenomenon(phenomenon, migratedWrites);
};

export const createPhenomenon = (phenomenon: TalosPayload<Phenomenon>, migratedWrites = '[]'): AxiosObservable<Phenomenon> => {
  if (hasOperation(migratedWrites, 'phenomena.create')) {
    return ObservationsConfigV2.createPhenomenon(phenomenon.payload);
  }

  return axios.post<Phenomenon>(`${phenomenonUrl}`, phenomenon, buildWorkspaceConfig(phenomenon.payload.workspace_id ?? undefined));
};

export const updatePhenomenon = (phenomenon: TalosPayload<Phenomenon>, migratedWrites = '[]'): AxiosObservable<Phenomenon> => {
  if (hasOperation(migratedWrites, 'phenomena.update')) {
    return ObservationsConfigV2.updatePhenomenon(phenomenon.payload);
  }

  return axios.put<Phenomenon>(`${phenomenonUrl}`, phenomenon, buildWorkspaceConfig(phenomenon.payload.workspace_id ?? undefined));
};

export const deletePhenomenon = (category: TalosPayload<Phenomenon>, migratedWrites = '[]'): AxiosObservable<Phenomenon> => {
  if (hasOperation(migratedWrites, 'phenomena.delete')) {
    const { id } = category.payload;
    if (!id) throw new Error('Phenomenon id is required');
    return ObservationsConfigV2.deletePhenomenon(id);
  }

  return axios.put<Phenomenon>(`${phenomenonUrl}/remove`, category);
};

export const clonePhenomenon = (
  phenomenonId: UUID,
  companyId: UUID,
  enableCottonGrowth?: boolean,
  migratedWrites = '[]'
): AxiosObservable<Phenomenon> => {
  if (hasOperation(migratedWrites, 'phenomena.clone-to-org')) {
    return ObservationsConfigV2.clonePhenomenonToOrg(phenomenonId, companyId);
  }

  return enableCottonGrowth
    ? axios.post(`${phenomenaUrl}/${phenomenonId}/clone-to-company/${companyId}`)
    : axios.post(`${phenomenonUrl}/${phenomenonId}/clone-to-company/${companyId}`);
};

export const clonePhenomenonToWorkspace = (phenomenonId: UUID, workspaceId: UUID, migratedWrites = '[]'): AxiosObservable<Phenomenon> => {
  if (hasOperation(migratedWrites, 'phenomena.clone-to-workspace')) {
    return ObservationsConfigV2.clonePhenomenonToWorkspace(phenomenonId, workspaceId);
  }

  return axios.post(`${phenomenaUrl}/${phenomenonId}/clone-to-workspace/${workspaceId}`, undefined, buildWorkspaceConfig(workspaceId));
};

export const cloneCharacteristicsIndicators = (
  itemsToClone: CommandRequest<CloneCharacteristicsIndicatorsDTO>,
  migratedWrites = '[]'
): AxiosObservable<ClonedCharacteristicsIndicators> => {
  if (hasOperation(migratedWrites, 'phenomena.clone-entities')) {
    return ObservationsConfigV2.clonePhenomenonEntities(itemsToClone.payload);
  }

  return axios.post<ClonedCharacteristicsIndicators>(
    `${phenomenaApiUrl}/clone-entities`,
    itemsToClone,
    buildWorkspaceConfig(itemsToClone.payload.workspace_id ?? undefined)
  );
};
